import { StoreLoadingEnum } from 'src/store/state/types';
import { FilesOption, FilesInitialState, UpdateExpireDateOptions, FilesOptionsHeaders } from './types';
import { FilesData } from 'src/components/FilesList/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../..';
import filesApi from './api';
import { ApiError, createApiError } from 'src/services/api/helper';
import { AxiosError } from 'axios';

/**
 * Initial State
 */

const initialState: FilesInitialState<FilesData> = {
  loading: StoreLoadingEnum.idle,
  error: null,
  data: {
    items: [],
    totalCount: 0,
  },
  isFilesUpload: false,
  options: {
    tusMaxSize: 0,
    tusMaxChunkSize: 0,
    tusResumable: '',
    tusVersion: '',
  },
};

/**
 * Async Actions
 */

let abortController: AbortController | null = null;

const abortPreviousRequest = () => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
};

export const optionsFiles = createAsyncThunk<FilesOptionsHeaders>('files/options', async (options, thunkApi) => {
  try {
    const response = await filesApi.fetchOptions();
    return {
      tusMaxSize: +response.headers['tus-max-size'],
      tusMaxChunkSize: +response.headers['tus-max-chunk-size'],
      tusResumable: response.headers['tus-resumable'],
      tusVersion: response.headers['tus-version'],
    };
  } catch (e) {
    return thunkApi.rejectWithValue(createApiError(e as AxiosError<ApiError>, true));
  }
});

export const fetchFiles = createAsyncThunk('files/fetch', async (options: FilesOption, thunkApi) => {
  abortPreviousRequest();
  try {
    const response = await filesApi.fetchFiles(options, { signal: abortController?.signal });
    return { data: { ...response.data, options } };
  } catch (e) {
    return thunkApi.rejectWithValue(createApiError(e as AxiosError<ApiError>, true));
  }
});

export const updateFile = createAsyncThunk('files/update', async (options: UpdateExpireDateOptions, thunkApi) => {
  try {
    const response = await filesApi.updateFileRequest(options);
    return response.data;
  } catch (e) {
    return thunkApi.rejectWithValue(createApiError(e as AxiosError<ApiError>));
  }
});

export const deleteFile = createAsyncThunk('files/delete', async (id: string, thunkApi) => {
  try {
    await filesApi.deleteFile(id);
  } catch (e) {
    return thunkApi.rejectWithValue(createApiError(e as AxiosError<ApiError>));
  }
});

export const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    setIsFilesUpload: (state, action) => {
      state.isFilesUpload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = StoreLoadingEnum.succeeded;
          const { data } = action.payload;
          state.data = data;
        }
      })
      .addCase(fetchFiles.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(deleteFile.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(deleteFile.fulfilled, (state) => {
        state.loading = StoreLoadingEnum.succeeded;
      })
      .addCase(deleteFile.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(optionsFiles.pending, (state) => {
        state.loading = StoreLoadingEnum.succeeded;
      })
      .addCase(optionsFiles.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = StoreLoadingEnum.succeeded;
          state.options = action.payload;
        }
      })
      .addCase(optionsFiles.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      });
  },
});

export const filesSelector = (state: RootState) => state.files.data;
export const isLoadingSelector = (state: RootState) =>
  state.files.loading === StoreLoadingEnum.pending;
export const isFileUploadedSelector = (state: RootState) => state.files.isFilesUpload;
export const { setIsFilesUpload } = filesSlice.actions;
export const filesOptionsSelector = (state: RootState) => state.files.options;
